import type { RouteRecordRaw } from 'vue-router';


import i18n from '@/i18n/i18n';
import AnotherUserPage from '@/pages/AnotherUserPage.vue';
import ExpiredUserPage from '@/pages/ExpiredUserPage.vue';
import ExternalProvider from '@/pages/ExternalProvider.vue';
import LoginPage from '@/pages/LoginPage.vue';
import Logout from '@/pages/LogoutPage.vue';
import NoAccessPage from '@/pages/NoAccessPage.vue';
import NoRightsPage from '@/pages/NoRightsPage.vue';
import PasswordRestore from '@/pages/PasswordRestore.vue';
import { useAuthStore } from '@/stores/auth/auth';
import { checkIfIframe } from '@/utils/checkIfIframe';
import { isExactFeature, SSO_TEST_FEATURE } from '@/utils/envCheckers';

import config from '../config';

const defaultLoginProvider = config.DEFAULT_LOGIN_PROVIDER;

const redirectPath = '/login';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: redirectPath,
  },
  {
    path: '/logout/:force?',
    component: Logout,
    name: 'logout',
    meta: {
      allowForceChangePassword: true,
    },
  },
  {
    path: '/login',
    component: LoginPage,
    name: 'login',
    props: { shouldRedirectToDefaultProvider: Boolean(defaultLoginProvider) },
  },
  {
    path: '/password-restore',
    name: 'password-restore',
    component: PasswordRestore,
  },
  {
    path: '/auth/:providerName',
    name: 'auth-provider',
    component: ExternalProvider,
  },
  {
    path: '/session-expired',
    name: 'session-expired',
    redirect: (to) => {
      const { anotherUser, noRights } = to.query;

      if (!anotherUser && !noRights) {
        return ({ path: '/expired-token' });
      } else if (anotherUser) {
        return ({ path: '/another-user' });
      }
      return ({ path: '/no-rights' });
    },
  },
  {
    path: '/expired-token',
    name: 'expired-token',
    component: ExpiredUserPage,
  },
  {
    path: '/no-rights',
    name: 'no-rights',
    component: NoRightsPage,
  },
  {
    path: '/no-access',
    name: 'no-access',
    component: NoAccessPage,
  },
  {
    path: '/another-user',
    name: 'another-user',
    component: AnotherUserPage,
  },
  {
    path: '/multi-user/list-users',
    name: 'multi-user-list-users',
    meta: {
      authMult: true,
    },
    component: () => import('../pages/UserList.vue'),
  },
  {
    path: '/multi-user/set-password',
    name: 'multi-user-set-password',
    component: () => import('../pages/CreateProfile.vue'),
  },
  {
    path: '/multi-user/set-profile',
    name: 'multi-user-set-profile',
    component: () => import('../pages/SetProfile.vue'),
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('../layouts/TheWrapper.vue'),
    meta: {
      auth: true,
      iframeEventRequired: true,
    },
    children: [
      {
        meta: {
          authMult: true,
          title: (() => i18n.global.t('profile.routes.Profile'))(),
          allowSuspended: true,
        },
        path: '',
        name: 'profile-my',
        component: () => import('../pages/UserSettings.vue'),
      },
      {
        meta: {
          authMult: true,
          title: (() => i18n.global.t('profile.routes.Security'))(),
          allowSuspended: true,
          allowForceChangePassword: true,
        },
        path: 'security',
        name: 'profile-security',
        component: () => import('../pages/SecuritySettings.vue'),
      },
    ],
  },
  {
    path: '/account',
    name: 'account',
    component: () => import('../layouts/TheWrapper.vue'),
    meta: {
      auth: true,
      iframeEventRequired: true,
    },
    children: [
      {
        path: '',
        name: 'account-home',
        redirect: () => ({ path: 'account/activity' }),
      },
      {
        meta: {
          title: (() => i18n.global.t('profile.routes.Activity'))(),
          authMult: true,
          allowSuspended: true,
        },
        path: 'activity',
        name: 'profile-activity',
        component: () => import('../pages/ActivityPage.vue'),
        beforeEnter: (to, from, next) => {
          if (!config.NOTIFICATIONS_API_URL.length) {
            next('/account/members');
          }
          next();
        },
      },
      {
        meta: {
          authMult: true,
          title: (() => i18n.global.t('profile.routes.Members'))(),
        },
        path: 'members',
        name: 'profile-members',
        component: () => import('../pages/ManageUsers.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore();

          if (authStore.role === 'GUEST') {
            next('/account');
          }
          next();
        },
      },
      {
        meta: {
          title: (() => i18n.global.t('profile.routes.Keys'))(),
          authMult: true,
        },
        path: 'keys',
        name: 'profile-keys',
        component: () => import('../pages/ManageKeys.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore();

          if (authStore.role === 'GUEST') {
            next('/account');
          }
          next();
        },
      },
      {
        meta: {
          title: (() => i18n.global.t('profile.routes.Payments'))(),
          authMult: true,
          allowSuspended: true,
        },
        path: 'payments',
        name: 'profile-payments',
        component: () => import('../pages/PaymentsPage.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore();

          if (!config.ENABLE_PAYMENTS || !authStore.isPayable || authStore.role === 'GUEST') {
            next('/account');
          }
          next();
        },
      },
    ],
  },
  {
    path: '/multi-user/change-email',
    name: 'multi-user-change-email',
    component: () => import('../pages/ChangeEmail.vue'),
  },

  {
    path: '/multi-user/sso',
    name: 'multi-user-sso',
    component: () => import('../pages/SsoPage.vue'),
  },
  {
    path: '/multi-user/sso-error',
    name: 'multi-user-sso-error',
    component: () => import('../pages/SsoError.vue'),
  },
  {
    path: '/activity',
    name: 'activity',
    meta: {
      auth: true,
    },
    redirect: () => {
      return ({ path: 'account/activity' });
    },
  },
  {
    path: '/or-auth',
    name: 'or-auth',
    meta: {
      authMult: true,
    },
    component: () => import('../pages/OrAuth.vue'),
  },

  // catch all redirect
  {
    path: '/:pathMatch(.*)*',
    redirect: redirectPath,
  },
];

routes.push({
  path: '/login-admin',
  component: LoginPage,
  name: 'login-admin',
  props: {
    shouldRedirectToDefaultProvider: false,
    showGoogleSSo: true,
  },
});

if (isExactFeature(SSO_TEST_FEATURE)) {
  routes.push({
    path: '/oauth_idp',
    name: 'oauth-idp',
    component: () => import('../pages/OAuthProvider.vue'),
  });
}

if (checkIfIframe()) {
  routes.push(...[
    {
      path: '/iframe',
      name: 'iframe',
      meta: {
        iframeEventRequired: true,
      },
      component: () => import('../pages/IframeLoader.vue'),
    },
    {
      path: '/validate-payment',
      name: 'validate-payment',
      component: () => import('../pages/PaymentsValidationIframe.vue'),
    },
  ]);
}

export default routes;
